import { useEffect, useRef } from "react";
import { BrowserRouter, useLocation } from "react-router";
import { useAuth } from "react-oidc-context";
import { createLogger } from "./features/notifications/utils/logger";
import ApplicationRoutes from "./routes/applicationRoutes";
import "./App.css";
import { useAppDispatch } from "./redux/hooks";
import { setAuthSyncComplete } from "./redux/user/userSlice";

const logger = createLogger("App");

const IDLE_TIMEOUT = (Number(import.meta.env.VITE_IDLE_TIMEOUT_MINUTE) || 30) * 60 * 1000;

const AppContent = () => {
  const location = useLocation();
  // Just for QA/DEV/localhost testing: keep URL id in session storage.
  useEffect(() => {
    const hostname = window.location.hostname;
    
    if (hostname.startsWith("qa") || hostname.startsWith("dev") || hostname.startsWith("localhost")) {
      const params = new URLSearchParams(location.search);
      const queryId = params.get("id");

      if (queryId) {
        sessionStorage.setItem("queryId", queryId);
         console.log("AppContent: queryId from URL:", queryId);
      } else {
        sessionStorage.removeItem("queryId");
      }
    }

    return () => {
      sessionStorage.removeItem("queryId");
    };
  }, [location.search]);
  return <ApplicationRoutes />;
};

const App = () => {
  const auth = useAuth();
  const dispatch = useAppDispatch();
  const idleTimer = useRef<number | null>(null);
  const isLoggingOut = useRef(false);
  const isRefreshingToken = useRef(false);
  const isRedirectingForAuth = useRef(false);

  useEffect(() => {
    try {
      sessionStorage.removeItem("UUID");
    } catch (error) {
      logger.warn("Failed to refresh session storage:", error);
    }
  }, []);

  useEffect(() => {
    if (auth.error) {
      dispatch(setAuthSyncComplete());
      return;
    }

    if (!auth.isLoading && auth.isAuthenticated) {
      const linkUuid = auth.user?.profile?.sub;
      if (linkUuid) {
        try {
          sessionStorage.setItem("UUID", linkUuid);
        } catch (error) {
          logger.warn("Failed to persist UUID in session storage:", error);
        }
      }
      dispatch(setAuthSyncComplete());
    }
  }, [auth.error, auth.isLoading, auth.isAuthenticated, auth.user, dispatch]);

  useEffect(() => {
    if (!auth.events) {
      return;
    }

    const renewTokenSilently = async (source: "expiring" | "silent-renew-error") => {
      if (isLoggingOut.current || isRefreshingToken.current || !auth.isAuthenticated) {
        return;
      }

      isRefreshingToken.current = true;
      try {
        await auth.signinSilent();
        logger.info(`Access token refreshed via silent renew (${source})`);
      } catch (error) {
        logger.error(`Silent token refresh failed (${source}):`, error);
      } finally {
        isRefreshingToken.current = false;
      }
    };

    const redirectToSignin = async (source: "expired" | "silent-renew-error") => {
      if (isLoggingOut.current || isRedirectingForAuth.current) {
        return;
      }

      isRedirectingForAuth.current = true;
      try {
        await auth.signinRedirect({ state: { returnTo: window.location.pathname } });
      } catch (error) {
        logger.error(`Signin redirect failed after token ${source}:`, error);
        isRedirectingForAuth.current = false;
      }
    };

    const onAccessTokenExpiring = () => {
      void renewTokenSilently("expiring");
    };

    const onAccessTokenExpired = () => {
      logger.warn("Access token expired; redirecting to sign-in");
      void redirectToSignin("expired");
    };

    const onSilentRenewError = () => {
      logger.warn("Silent renew reported an error; attempting one immediate silent refresh");
      void renewTokenSilently("silent-renew-error").then(() => {
        if (!auth.isAuthenticated) {
          void redirectToSignin("silent-renew-error");
        }
      });
    };

    const onUserLoaded = () => {
      logger.debug("OIDC user session updated in storage");
    };

    auth.events.addAccessTokenExpiring(onAccessTokenExpiring);
    auth.events.addAccessTokenExpired(onAccessTokenExpired);
    auth.events.addSilentRenewError(onSilentRenewError);
    auth.events.addUserLoaded(onUserLoaded);

    return () => {
      auth.events.removeAccessTokenExpiring(onAccessTokenExpiring);
      auth.events.removeAccessTokenExpired(onAccessTokenExpired);
      auth.events.removeSilentRenewError(onSilentRenewError);
      auth.events.removeUserLoaded(onUserLoaded);
    };
  }, [auth]);

  useEffect(() => {
    // Only set up idle timer if user is authenticated
    if (!auth.isAuthenticated || auth.isLoading) {
      return;
    }

    const resetTimer = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => {
        // Prevent multiple simultaneous logout attempts
        if (!isLoggingOut.current && auth.isAuthenticated) {
          isLoggingOut.current = true;
          auth.signoutRedirect().catch((error) => {
            logger.error("Signout redirect failed:", error);
            isLoggingOut.current = false;
          });
        }
      }, IDLE_TIMEOUT);
    };

    const events = ["mousemove", "keydown", "mousedown", "touchstart"];
    events.forEach((event) => window.addEventListener(event, resetTimer));
    resetTimer();

    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      events.forEach((event) => window.removeEventListener(event, resetTimer));
    };
  }, [auth.isAuthenticated, auth.isLoading]);

  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
};

export default App;
